import axios from "axios";
import { BASE_API } from "@/constants";

// Cache for country codes
let countryCodesCache = null;

// Fallback country codes
const FALLBACK_CODES = [
    { value: "62", title: "Indonesia", iso: "ID" },
    { value: "61", title: "Australia", iso: "AU" },
    { value: "60", title: "Malaysia", iso: "MY" },
    { value: "65", title: "Singapore", iso: "SG" },
    { value: "1", title: "United States", iso: "US" },
];

/**
 * Fetch country codes from API (cached)
 */
const fetchCountryCodes = async () => {
    if (countryCodesCache) return countryCodesCache;

    try {
        const response = await axios.get(`${BASE_API}/select/country-code`);
        if (response.data?.data) {
            countryCodesCache = response.data.data;
            return countryCodesCache;
        }
    } catch (error) {
        console.warn("Failed to fetch country codes, using fallback");
    }

    countryCodesCache = FALLBACK_CODES;
    return countryCodesCache;
};

// Pre-fetch on module load
fetchCountryCodes();

/**
 * Format Indonesian phone number for display
 * Supports: 08xx, +628xx, 628xx formats
 *
 * @param {string} phone - Raw phone number
 * @returns {string} Formatted phone number (e.g., "0812-3456-7890")
 */
export const formatPhoneNumber = (phone) => {
    if (!phone) return "-";

    // Remove all non-digit characters except +
    let cleaned = phone.replace(/[^\d+]/g, "");

    // Convert +62 or 62 prefix to 0
    if (cleaned.startsWith("+62")) {
        cleaned = "0" + cleaned.slice(3);
    } else if (cleaned.startsWith("62")) {
        cleaned = "0" + cleaned.slice(2);
    }

    // Format based on length
    const digits = cleaned.replace(/\D/g, "");

    if (digits.length <= 4) {
        return digits;
    } else if (digits.length <= 8) {
        return `${digits.slice(0, 4)}-${digits.slice(4)}`;
    } else if (digits.length <= 12) {
        return `${digits.slice(0, 4)}-${digits.slice(4, 8)}-${digits.slice(8)}`;
    } else {
        return digits.match(/.{1,4}/g).join("-");
    }
};

/**
 * Parse phone number to detect country code
 * @param {string} phone - Phone number (e.g., "6281234567890")
 * @returns {{ countryCode: string, number: string }}
 */
const parsePhoneWithCountryCode = (phone) => {
    if (!phone) return { countryCode: "+62", number: "" };

    const numStr = String(phone).replace(/[^\d]/g, "");
    const codes = countryCodesCache || FALLBACK_CODES;

    // Sort by code length DESC for proper matching
    const sortedCodes = [...codes].sort(
        (a, b) => String(b.value).length - String(a.value).length,
    );

    for (const cc of sortedCodes) {
        const codeValue = String(cc.value);
        if (numStr.startsWith(codeValue)) {
            return {
                countryCode: `+${codeValue}`,
                number: numStr.slice(codeValue.length),
            };
        }
    }

    // Default to showing as-is with Indonesia code
    return { countryCode: "+62", number: numStr };
};

/**
 * Format phone number with auto-detected country code for international display
 *
 * @param {string} phone - Raw phone number (e.g., "6281234567890" or "6182345678")
 * @returns {string} Formatted phone with country code (e.g., "+62 812-3456-7890" or "+61 823-4567-8")
 */
export const formatPhoneInternational = (phone) => {
    if (!phone) return "-";

    const { countryCode, number } = parsePhoneWithCountryCode(phone);

    // Remove leading 0 if present
    let cleaned = number;
    if (cleaned.startsWith("0")) {
        cleaned = cleaned.slice(1);
    }

    // Format the number part with dashes
    if (cleaned.length <= 3) {
        return `${countryCode} ${cleaned}`;
    } else if (cleaned.length <= 7) {
        return `${countryCode} ${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    } else {
        return `${countryCode} ${cleaned.slice(0, 3)}-${cleaned.slice(3, 7)}-${cleaned.slice(7)}`;
    }
};
